import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  Rocket,
  Zap,
  CheckCircle2,
  ShieldCheck,
} from "lucide-react";
import {
  OnboardingRightPanelData,
  OnboardingState,
  onboardingService,
} from "../../services/OnboardingService";

interface OnboardingRightPanelProps {
  component: string | null;
  state: OnboardingState;
  rightPanel?: OnboardingRightPanelData | null;
  onAction?: (action: string, value?: string) => void;
  onSetValue: (
    field: string,
    value: any,
    options?: { skipUserMessage?: boolean },
  ) => Promise<void>;
  onClose: () => void;
  onLifecycleSelectionChange?: (value: string, label: string) => void;
  onContractCreationSelectionChange?: (
    pipelineId: string,
    pipelineLabel: string,
    stageId: string,
    stageLabel: string,
  ) => void;
  onRenewalSelectionChange?: (
    pipelineId: string,
    pipelineLabel: string,
    stageId: string,
    stageLabel: string,
    daysBeforeExpiration: number,
  ) => void;
  onSubscriptionSelectionChange?: (productId: string | null) => void;
  onPendingSubscriptionStartDateChange?: (value: string | null) => void;
  onSaveSubscriptionBilling?: (billing: {
    collectionMethod: "charge_automatically" | "send_invoice";
    daysUntilDue?: number;
    prorationBehavior: "none" | "create_prorations" | "always_invoice";
  }) => Promise<void>;
}

// Enable RevSync Panel Component
const EnableRevSyncPanel: React.FC<{ state: OnboardingState }> = ({
  state,
}) => {
  const settings = state?.settings || {};
  const features = [
    {
      icon: CheckCircle2,
      title: "Customer Sync",
      description: settings.customerLifecycleStage
        ? `Lifecycle stage: ${settings.customerLifecycleStage}`
        : "Customer lifecycle configured",
    },
    {
      icon: CheckCircle2,
      title: "Contract Automation",
      description: settings.netNewDealPipelineId
        ? "Auto-create contracts enabled"
        : "Contract creation ready",
    },
    {
      icon: CheckCircle2,
      title: "Subscription Management",
      description: settings.autoCreateSubscriptions
        ? "Auto-create subscriptions enabled"
        : "Subscription sync configured",
    },
    {
      icon: CheckCircle2,
      title: "Renewal Tracking",
      description: settings.enableRenewalDeals
        ? "Renewal deals enabled"
        : "Renewal settings configured",
    },
  ];

  return (
    <div className="h-full flex flex-col items-center justify-center px-8 py-12">
      <div className="max-w-md w-full space-y-8">
        {/* Animated Icon */}
        <div className="flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl animate-pulse" />
            <div className="relative bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-2xl shadow-lg">
              <Rocket className="w-12 h-12 text-white" />
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-text-primary dark:text-text-primaryDark">
            You're All Set!
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
            RevSync is ready to automate your revenue operations
          </p>
        </div>

        {/* Features List */}
        <div className="space-y-3">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="flex items-start gap-3 p-3 rounded-lg bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark"
                style={{
                  animation: `fadeIn 0.5s ease-out ${index * 0.1}s both`,
                }}
              >
                <Icon className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
                    {feature.title}
                  </div>
                  <div className="text-xs text-text-secondary dark:text-text-secondaryDark mt-0.5">
                    {feature.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Message */}
        <div className="text-center pt-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-panel dark:bg-background-panelDark border border-divider dark:border-divider-dark">
            <Zap className="w-4 h-4 text-green-500 dark:text-green-400" />
            <span className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
              Ready to activate
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

const EnableSuccessPanel: React.FC = () => {
  return (
    <div className="h-full flex flex-col items-center justify-center px-8 py-12">
      <div className="max-w-md w-full space-y-6">
        <div className="flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl animate-pulse" />
            <div className="relative bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-2xl shadow-lg">
              <ShieldCheck className="w-12 h-12 text-white" />
            </div>
          </div>
        </div>
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-text-primary dark:text-text-primaryDark">
            RevSync is enabled
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
            Your automations are live and monitoring your revenue pipeline.
          </p>
        </div>
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-panel dark:bg-background-panelDark border border-divider dark:border-divider-dark">
            <Zap className="w-4 h-4 text-green-500 dark:text-green-400" />
            <span className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
              Live monitoring active
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const OnboardingRightPanel: React.FC<OnboardingRightPanelProps> = ({
  component,
  state,
  rightPanel,
  onAction,
  onSetValue,
  onClose,
  onLifecycleSelectionChange,
  onContractCreationSelectionChange,
  onRenewalSelectionChange,
  onSubscriptionSelectionChange,
  onPendingSubscriptionStartDateChange,
  onSaveSubscriptionBilling,
}) => {
  if (!component) return null;

  return (
    <div className="h-full w-full bg-background-paper dark:bg-background-paperDark border-l border-divider dark:border-divider-dark p-6 overflow-y-auto flex flex-col">
      {component === "lifecycle_stage_selector" && (
        <LifecycleStageSelector
          state={state}
          onSetValue={onSetValue}
          onClose={onClose}
          onSelectionChange={onLifecycleSelectionChange}
        />
      )}
      {component === "contract_creation_selector" && (
        <ContractCreationSelector
          state={state}
          onSetValue={onSetValue}
          onClose={onClose}
          onSelectionChange={onContractCreationSelectionChange}
        />
      )}
      {component === "renewal_settings_selector" && (
        <RenewalSettingsSelector
          state={state}
          onSetValue={onSetValue}
          onClose={onClose}
          onSelectionChange={onRenewalSelectionChange}
        />
      )}
      {(component === "subscription_creation_selector" ||
        component === "subscription_selector" ||
        component === "subscription_creation" ||
        component === "subscription_product_selector") && (
        <SubscriptionCreationSelector
          state={state}
          rightPanel={rightPanel}
          onSetValue={onSetValue}
          onClose={onClose}
          onSelectionChange={onSubscriptionSelectionChange}
          onAction={onAction}
          onPendingStartDateChange={onPendingSubscriptionStartDateChange}
          onSaveSubscriptionBilling={onSaveSubscriptionBilling}
          component={component}
        />
      )}
      {component === "enable_revsync" && <EnableRevSyncPanel state={state} />}
      {component === "enable_success" && <EnableSuccessPanel />}
    </div>
  );
};

interface SelectorProps {
  state: OnboardingState;
  onSetValue: (
    field: string,
    value: any,
    options?: { skipUserMessage?: boolean },
  ) => Promise<void>;
  onClose: () => void;
}

interface ContractCreationSelectorProps extends SelectorProps {
  onSelectionChange?: (
    pipelineId: string,
    pipelineLabel: string,
    stageId: string,
    stageLabel: string,
  ) => void;
}

interface RenewalSettingsSelectorProps extends SelectorProps {
  onSelectionChange?: (
    pipelineId: string,
    pipelineLabel: string,
    stageId: string,
    stageLabel: string,
    daysBeforeExpiration: number,
  ) => void;
}

interface LifecycleStageSelectorProps extends SelectorProps {
  onSelectionChange?: (value: string, label: string) => void;
}

const LifecycleStageSelector: React.FC<LifecycleStageSelectorProps> = ({
  state,
  onSetValue,
  onClose,
  onSelectionChange,
}) => {
  const [selected, setSelected] = useState(
    state.settings.customerLifecycleStage || "",
  );

  useEffect(() => {
    setSelected(state.settings.customerLifecycleStage || "");
  }, [state.settings.customerLifecycleStage]);

  const stages = state.lifecycleStages || [];
  const handleSelectionChange = (value: string) => {
    setSelected(value);
    const selectedStage = stages.find((stage) => stage.value === value);
    if (selectedStage) {
      onSelectionChange?.(selectedStage.value, selectedStage.label);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
          Select Customer Lifecycle Stage
        </h3>
        <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
          Choose which HubSpot lifecycle stage defines a customer for RevSync.
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
          Lifecycle Stage
        </label>
        <div className="relative">
          <select
            value={selected}
            onChange={(e) => handleSelectionChange(e.target.value)}
            className="w-full px-3 py-2 pr-10 text-sm rounded-md border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark appearance-none"
          >
            <option value="">Select a stage...</option>
            {stages.map((stage) => (
              <option key={stage.value} value={stage.value}>
                {stage.label}
                {stage.value === state.settings.customerLifecycleStage
                  ? " (current)"
                  : ""}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark pointer-events-none" />
        </div>
      </div>
    </div>
  );
};

type SubscriptionBilling = {
  collectionMethod: "charge_automatically" | "send_invoice";
  daysUntilDue?: number;
  prorationBehavior: "none" | "create_prorations" | "always_invoice";
};

const defaultSubscriptionBilling: SubscriptionBilling = {
  collectionMethod: "send_invoice",
  daysUntilDue: 30,
  prorationBehavior: "none",
};

const SubscriptionCreationSelector: React.FC<
  SelectorProps & {
    component?: string | null;
    rightPanel?: OnboardingRightPanelData | null;
    onSelectionChange?: (productId: string | null) => void;
    onAction?: (action: string, value?: string) => void;
    onPendingStartDateChange?: (value: string | null) => void;
    onSaveSubscriptionBilling?: (billing: SubscriptionBilling) => Promise<void>;
  }
> = ({ state, rightPanel, component, onSelectionChange, onSetValue, onAction, onPendingStartDateChange, onSaveSubscriptionBilling }) => {
  const dealDateFields = rightPanel?.dealDateFields ?? state.dealDateFields ?? [];
  const stripeProducts = rightPanel?.stripeProducts ?? state.stripeProducts ?? [];
  const productsWithDefaultPrice = stripeProducts.filter((product) => {
    if ((product as { defaultPriceId?: string }).defaultPriceId) return true;
    const defaultPrice = product.default_price;
    if (!defaultPrice) return false;
    if (typeof defaultPrice === "string") return true;
    return defaultPrice.active !== false;
  });
  const selectedProductId =
    state.subscriptionProductMapping?.defaultProductId ||
    state.settings.subscriptionProductMapping?.defaultProductId ||
    "";
  const initialStartDateField =
    state.subscriptionSetup?.fieldMappings?.startDate?.field ||
    state.settings.subscriptionSetup?.fieldMappings?.startDate?.field ||
    "";
  const [startDateField, setStartDateField] = useState(initialStartDateField);
  const [selectedProduct, setSelectedProduct] = useState(selectedProductId);

  const billingFromState = rightPanel?.subscriptionBilling ?? state.subscriptionBilling;
  const [billing, setBilling] = useState<SubscriptionBilling>({
    collectionMethod: billingFromState?.collectionMethod ?? defaultSubscriptionBilling.collectionMethod,
    daysUntilDue: billingFromState?.daysUntilDue ?? defaultSubscriptionBilling.daysUntilDue,
    prorationBehavior: billingFromState?.prorationBehavior ?? defaultSubscriptionBilling.prorationBehavior,
  });
  const [daysUntilDueInput, setDaysUntilDueInput] = useState(
    billingFromState?.collectionMethod === "send_invoice" && billingFromState?.daysUntilDue != null
      ? String(billingFromState.daysUntilDue)
      : "",
  );
  const daysUntilDueInputFocusedRef = useRef(false);

  useEffect(() => {
    setStartDateField(
      state.subscriptionSetup?.fieldMappings?.startDate?.field ||
        state.settings.subscriptionSetup?.fieldMappings?.startDate?.field ||
        "",
    );
    setSelectedProduct(
      state.subscriptionProductMapping?.defaultProductId ||
        state.settings.subscriptionProductMapping?.defaultProductId ||
        "",
    );
  }, [
    state.subscriptionProductMapping,
    state.settings.subscriptionProductMapping,
    state.subscriptionSetup?.fieldMappings?.startDate?.field,
    state.settings.subscriptionSetup?.fieldMappings?.startDate?.field,
  ]);

  useEffect(() => {
    setBilling({
      collectionMethod: billingFromState?.collectionMethod ?? defaultSubscriptionBilling.collectionMethod,
      daysUntilDue: billingFromState?.daysUntilDue ?? defaultSubscriptionBilling.daysUntilDue,
      prorationBehavior: billingFromState?.prorationBehavior ?? defaultSubscriptionBilling.prorationBehavior,
    });
  }, [
    billingFromState?.collectionMethod,
    billingFromState?.daysUntilDue,
    billingFromState?.prorationBehavior,
  ]);

  useEffect(() => {
    if (daysUntilDueInputFocusedRef.current) return;
    if (billing.collectionMethod === "send_invoice" && billing.daysUntilDue != null) {
      setDaysUntilDueInput(String(billing.daysUntilDue));
    } else {
      setDaysUntilDueInput("");
    }
  }, [billing.collectionMethod, billing.daysUntilDue]);

  const handleStartDateChange = (value: string) => {
    setStartDateField(value);
    onPendingStartDateChange?.(value || null);
  };

  const handleProductChange = (productId: string) => {
    const nextId = productId || "";
    setSelectedProduct(nextId);
    onSelectionChange?.(nextId || null);
    if (nextId) onAction?.("set_subscription_product", nextId);
  };

  const handleBillingChange = (updates: Partial<SubscriptionBilling>) => {
    let next: SubscriptionBilling = { ...billing, ...updates };
    if (
      next.collectionMethod === "send_invoice" &&
      next.daysUntilDue != null &&
      (next.daysUntilDue < 1 || next.daysUntilDue > 365)
    ) {
      next = { ...next, daysUntilDue: Math.max(1, Math.min(365, next.daysUntilDue ?? 30)) };
    }
    if (next.collectionMethod === "charge_automatically") {
      next = { ...next, daysUntilDue: undefined };
    }
    setBilling(next);
    onSaveSubscriptionBilling?.(next);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
          Stripe subscriptions
        </h3>
        <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
          RevSync creates subscriptions from deal line items.
        </p>
      </div>

      <div className="rounded-lg border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark p-4 space-y-3">
        <div>
          <div className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
            Stripe product (required)
          </div>
          <div className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1">
            Select a Stripe product with an active default price.
          </div>
        </div>
        {productsWithDefaultPrice.length > 0 ? (
          <div className="relative">
            <select
              value={selectedProduct}
              onChange={(e) => handleProductChange(e.target.value)}
              className="w-full px-3 py-2 pr-10 text-sm rounded-md border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark appearance-none"
            >
              <option value="">Select a product...</option>
              {productsWithDefaultPrice.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                  {product.description ? ` — ${product.description}` : ""}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark pointer-events-none" />
          </div>
        ) : (
          <div className="text-xs text-text-secondary dark:text-text-secondaryDark">
            No Stripe products found. Create one in Stripe with an active price
            and refresh to continue.
          </div>
        )}
      </div>

      <div className="rounded-lg border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark p-4 space-y-3">
        <div>
          <div className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
            Subscription billing
          </div>
          <div className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1">
            Collection and proration for Stripe subscriptions.
          </div>
        </div>
        <div className="space-y-2">
          <span className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
            Collection
          </span>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 text-sm text-text-primary dark:text-text-primaryDark">
              <input
                type="radio"
                name="subscription-billing-collection"
                checked={billing.collectionMethod === "charge_automatically"}
                onChange={() =>
                  handleBillingChange({
                    collectionMethod: "charge_automatically",
                    daysUntilDue: undefined,
                  })
                }
                className="rounded border-divider dark:border-divider-dark"
              />
              Charge automatically
            </label>
            <label className="flex items-center gap-2 text-sm text-text-primary dark:text-text-primaryDark">
              <input
                type="radio"
                name="subscription-billing-collection"
                checked={billing.collectionMethod === "send_invoice"}
                onChange={() =>
                  handleBillingChange({
                    collectionMethod: "send_invoice",
                    daysUntilDue: billing.daysUntilDue ?? 30,
                  })
                }
                className="rounded border-divider dark:border-divider-dark"
              />
              Send invoice
            </label>
          </div>
        </div>
        {billing.collectionMethod === "send_invoice" && (
          <div>
            <label className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
              Days until due (1–365)
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={daysUntilDueInput}
              onFocus={() => {
                daysUntilDueInputFocusedRef.current = true;
              }}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^\d+$/.test(v)) setDaysUntilDueInput(v);
              }}
              onBlur={() => {
                daysUntilDueInputFocusedRef.current = false;
                const s = daysUntilDueInput.trim();
                if (s === "") {
                  handleBillingChange({ daysUntilDue: 30 });
                  setDaysUntilDueInput("30");
                  return;
                }
                const n = parseInt(s, 10);
                if (Number.isFinite(n) && n >= 1 && n <= 365) {
                  handleBillingChange({ daysUntilDue: n });
                  setDaysUntilDueInput(String(n));
                } else {
                  handleBillingChange({ daysUntilDue: 30 });
                  setDaysUntilDueInput("30");
                }
              }}
              placeholder="30"
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark"
            />
          </div>
        )}
        <div>
          <label className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
            Proration
          </label>
          <div className="relative mt-1">
            <select
              value={billing.prorationBehavior}
              onChange={(e) =>
                handleBillingChange({
                  prorationBehavior: e.target.value as SubscriptionBilling["prorationBehavior"],
                })
              }
              className="w-full px-3 py-2 pr-10 text-sm rounded-md border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark appearance-none"
            >
              <option value="none">None</option>
              <option value="create_prorations">Create prorations</option>
              <option value="always_invoice">Always invoice</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark p-4">
        <div className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
          Use line items
        </div>
        <div className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1">
          Subscriptions are always created from deal line items automatically.
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
          Contract Start Date (Deal Field)
        </div>
        <div className="text-xs text-text-secondary dark:text-text-secondaryDark">
          Select the deal property that defines subscription start date.
        </div>
        <div className="space-y-3 pt-2">
          <div>
            <label className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
              Start Date Field
            </label>
            <div className="relative mt-1">
              <select
                value={startDateField}
                onChange={(e) => handleStartDateChange(e.target.value)}
                className="w-full px-3 py-2 pr-10 text-sm rounded-md border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark appearance-none"
              >
                <option value="">
                  {dealDateFields.length
                    ? "Select start date field..."
                    : "No date fields available"}
                </option>
                {dealDateFields.map((field) => (
                  <option key={field.name} value={field.name}>
                    {field.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark pointer-events-none" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContractCreationSelector: React.FC<ContractCreationSelectorProps> = ({
  state,
  onSetValue,
  onClose,
  onSelectionChange,
}) => {
  const [pipelineId, setPipelineId] = useState(
    state.settings.netNewDealPipelineId || "",
  );
  const [stageId, setStageId] = useState(
    state.settings.netNewDealStageId || "",
  );
  const [pipelines, setPipelines] = useState(
    state.pipelines ? [...state.pipelines] : [],
  );
  const [stages, setStages] = useState<Array<{ id: string; label: string }>>(
    [],
  );
  const [isLoadingStages, setIsLoadingStages] = useState(false);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);

  useEffect(() => {
    setPipelineId(state.settings.netNewDealPipelineId || "");
    setStageId(state.settings.netNewDealStageId || "");
    if (state.pipelines && state.pipelines.length) {
      setPipelines([...state.pipelines]);
    }
  }, [state.settings.netNewDealPipelineId, state.settings.netNewDealStageId]);

  useEffect(() => {
    let isMounted = true;
    if (pipelines.length) return;

    const loadPipelines = async () => {
      try {
        setIsLoadingPipelines(true);
        const response = await onboardingService.getPipelines();
        if (isMounted) {
          setPipelines(response.data.pipelines || []);
        }
      } catch (error) {
        console.error("Failed to load pipelines:", error);
        if (isMounted) {
          setPipelines([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingPipelines(false);
        }
      }
    };

    loadPipelines();
    return () => {
      isMounted = false;
    };
  }, [pipelines.length]);

  useEffect(() => {
    let isMounted = true;
    if (!pipelineId) {
      setStages([]);
      setStageId("");
      return;
    }

    const loadStages = async () => {
      try {
        setIsLoadingStages(true);
        const response = await onboardingService.getPipelineStages(pipelineId);
        if (isMounted) {
          setStages(response.data.stages || []);
        }
      } catch (error) {
        console.error("Failed to load stages:", error);
        if (isMounted) {
          setStages([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingStages(false);
        }
      }
    };

    loadStages();
    return () => {
      isMounted = false;
    };
  }, [pipelineId]);

  const isSaveDisabled = !pipelineId || !stageId;

  const handlePipelineChange = (newPipelineId: string) => {
    setPipelineId(newPipelineId);
    if (newPipelineId && stageId) {
      const selectedPipeline = pipelines.find((p) => p.id === newPipelineId);
      const selectedStage = stages.find((s) => s.id === stageId);
      if (selectedPipeline && selectedStage) {
        onSelectionChange?.(
          newPipelineId,
          selectedPipeline.label,
          stageId,
          selectedStage.label,
        );
      }
    }
  };

  const handleStageChange = (newStageId: string) => {
    setStageId(newStageId);
    if (pipelineId && newStageId) {
      const selectedPipeline = pipelines.find((p) => p.id === pipelineId);
      const selectedStage = stages.find((s) => s.id === newStageId);
      if (selectedPipeline && selectedStage) {
        onSelectionChange?.(
          pipelineId,
          selectedPipeline.label,
          newStageId,
          selectedStage.label,
        );
      }
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
          RevSync contracts
        </h3>
        <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
          Select the pipeline and stage where contracts should be created.
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
          Pipeline
        </label>
        <div className="relative">
          <select
            value={pipelineId}
            onChange={(e) => handlePipelineChange(e.target.value)}
            className="w-full px-3 py-2 pr-10 text-sm rounded-md border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark appearance-none"
          >
            <option value="">
              {isLoadingPipelines
                ? "Loading pipelines..."
                : "Select pipeline..."}
            </option>
            {pipelines.map((pipeline) => (
              <option key={pipeline.id} value={pipeline.id}>
                {pipeline.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark pointer-events-none" />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
          Stage
        </label>
        <div className="relative">
          <select
            value={stageId}
            onChange={(e) => handleStageChange(e.target.value)}
            disabled={!pipelineId || isLoadingStages}
            className="w-full px-3 py-2 pr-10 text-sm rounded-md border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark disabled:opacity-60 appearance-none"
          >
            <option value="">
              {isLoadingStages ? "Loading stages..." : "Select stage..."}
            </option>
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark pointer-events-none" />
        </div>
      </div>
    </div>
  );
};

const RenewalSettingsSelector: React.FC<RenewalSettingsSelectorProps> = ({
  state,
  onSetValue,
  onClose,
  onSelectionChange,
}) => {
  const [pipelineId, setPipelineId] = useState(
    state.settings.renewalPipelineId || "",
  );
  const [stageId, setStageId] = useState(state.settings.renewalStageId || "");
  const [daysBeforeExpiration, setDaysBeforeExpiration] = useState(
    String(state.settings.renewalDaysBeforeExpiration ?? 60),
  );
  const [pipelines, setPipelines] = useState(
    state.pipelines ? [...state.pipelines] : [],
  );
  const [stages, setStages] = useState<Array<{ id: string; label: string }>>(
    [],
  );
  const [isLoadingStages, setIsLoadingStages] = useState(false);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);

  useEffect(() => {
    setPipelineId(state.settings.renewalPipelineId || "");
    setStageId(state.settings.renewalStageId || "");
    setDaysBeforeExpiration(
      String(state.settings.renewalDaysBeforeExpiration ?? 60),
    );
    if (state.pipelines && state.pipelines.length) {
      setPipelines([...state.pipelines]);
    }
  }, [
    state.settings.renewalPipelineId,
    state.settings.renewalStageId,
    state.settings.renewalDaysBeforeExpiration,
  ]);

  useEffect(() => {
    let isMounted = true;
    if (pipelines.length) return;

    const loadPipelines = async () => {
      try {
        setIsLoadingPipelines(true);
        const response = await onboardingService.getPipelines();
        if (isMounted) {
          setPipelines(response.data.pipelines || []);
        }
      } catch (error) {
        console.error("Failed to load pipelines:", error);
        if (isMounted) {
          setPipelines([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingPipelines(false);
        }
      }
    };

    loadPipelines();
    return () => {
      isMounted = false;
    };
  }, [pipelines.length]);

  useEffect(() => {
    let isMounted = true;
    if (!pipelineId) {
      setStages([]);
      setStageId("");
      return;
    }

    const loadStages = async () => {
      try {
        setIsLoadingStages(true);
        const response = await onboardingService.getPipelineStages(pipelineId);
        if (isMounted) {
          setStages(response.data.stages || []);
        }
      } catch (error) {
        console.error("Failed to load renewal stages:", error);
        if (isMounted) {
          setStages([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingStages(false);
        }
      }
    };

    loadStages();
    return () => {
      isMounted = false;
    };
  }, [pipelineId]);

  const parsedDays = useMemo(
    () => Math.max(1, Number(daysBeforeExpiration) || 1),
    [daysBeforeExpiration],
  );

  const handlePipelineChange = (newPipelineId: string) => {
    setPipelineId(newPipelineId);
    if (newPipelineId && stageId) {
      const selectedPipeline = pipelines.find((p) => p.id === newPipelineId);
      const selectedStage = stages.find((s) => s.id === stageId);
      if (selectedPipeline && selectedStage) {
        onSelectionChange?.(
          newPipelineId,
          selectedPipeline.label,
          stageId,
          selectedStage.label,
          parsedDays,
        );
      }
    }
  };

  const handleStageChange = (newStageId: string) => {
    setStageId(newStageId);
    if (pipelineId && newStageId) {
      const selectedPipeline = pipelines.find((p) => p.id === pipelineId);
      const selectedStage = stages.find((s) => s.id === newStageId);
      if (selectedPipeline && selectedStage) {
        onSelectionChange?.(
          pipelineId,
          selectedPipeline.label,
          newStageId,
          selectedStage.label,
          parsedDays,
        );
      }
    }
  };

  const handleDaysChange = (newDays: string) => {
    setDaysBeforeExpiration(newDays);
    const newParsedDays = Math.max(1, Number(newDays) || 1);
    if (pipelineId && stageId) {
      const selectedPipeline = pipelines.find((p) => p.id === pipelineId);
      const selectedStage = stages.find((s) => s.id === stageId);
      if (selectedPipeline && selectedStage) {
        onSelectionChange?.(
          pipelineId,
          selectedPipeline.label,
          stageId,
          selectedStage.label,
          newParsedDays,
        );
      }
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
          Configure Renewal Deals
        </h3>
        <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
          Choose where renewal deals should be created and when they trigger.
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
          Renewal Pipeline
        </label>
        <div className="relative">
          <select
            value={pipelineId}
            onChange={(e) => handlePipelineChange(e.target.value)}
            className="w-full px-3 py-2 pr-10 text-sm rounded-md border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark appearance-none"
          >
            <option value="">
              {isLoadingPipelines
                ? "Loading pipelines..."
                : "Select pipeline..."}
            </option>
            {pipelines.map((pipeline) => (
              <option key={pipeline.id} value={pipeline.id}>
                {pipeline.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark pointer-events-none" />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
          Renewal Stage
        </label>
        <div className="relative">
          <select
            value={stageId}
            onChange={(e) => handleStageChange(e.target.value)}
            disabled={!pipelineId || isLoadingStages}
            className="w-full px-3 py-2 pr-10 text-sm rounded-md border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark disabled:opacity-60 appearance-none"
          >
            <option value="">
              {isLoadingStages ? "Loading stages..." : "Select stage..."}
            </option>
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark pointer-events-none" />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
          Days Before Expiration
        </label>
        <input
          type="number"
          min={1}
          max={365}
          value={daysBeforeExpiration}
          onChange={(e) => handleDaysChange(e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-md border border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark"
        />
      </div>
    </div>
  );
};

export default OnboardingRightPanel;
